import { useCallback, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { createOneTapCard } from "@/lib/card-generator";

/** Generated media lives behind our own API, which needs the backend origin. */
const absoluteUrl = (path) =>
  path?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${path}` : path;

/**
 * useChatMedia — inline media generation (image / video / sound) for chat.
 *
 * All handlers push a "loading" placeholder assistant bubble first, then
 * swap it out with the real result (or a warm error state) once the API
 * settles.
 *
 * Parameters:
 *   setMsgs   — the ChatScreen's message list setter.
 *   setBusy   — reflects an in-flight generation to disable input.
 *   user      — used for the "share as card" flow.
 *
 * Returns:
 *   { createImage, createVideo, createSound, handleInlineGenerate,
 *     downloadSound, downloadVideo, keepImageMemory, shareImageAsCard,
 *     onImageEdited, sharingIdx }
 */
export default function useChatMedia({ setMsgs, setBusy, user }) {
  const [sharingIdx, setSharingIdx] = useState(-1);

  const createImage = useCallback(async (prompt, { size = "1024x1024", inline = false, references = [] } = {}) => {
    setMsgs(m => [
      ...m,
      ...(inline ? [] : [{ role: "user", content: `Create: ${prompt}` }]),
      { role: "assistant", content: "", streaming: true, imageLoading: true, imagePrompt: prompt },
    ]);
    setBusy(true);
    try {
      const { data } = await api.post("/create/image", {
        prompt, size,
        // Pictures to work *from* — Rammy routes these to the model that can
        // keep the subject recognisable.
        reference_images: (references || []).slice(0, 4),
      });
      // Prefer the stored URL: it survives the tab, keeps the conversation
      // document small, and is the same file that lands in Creations.
      const src = data.url ? absoluteUrl(data.url) : `data:${data.mime};base64,${data.image_base64}`;
      const cast = data.cast_used || [];
      // If they're working from a photo but haven't saved that subject yet,
      // offer it — once per session, and never in the way. Being asked every
      // time is nagging; being asked once is helpful.
      let castHint = false;
      if (references?.length && cast.length === 0) {
        try {
          castHint = !sessionStorage.getItem("rammy-cast-hint");
          if (castHint) sessionStorage.setItem("rammy-cast-hint", "1");
        } catch (_) { castHint = false; }
      }
      setMsgs(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.imageLoading) {
          copy[copy.length - 1] = {
            role: "assistant",
            content: references?.length
              ? "Here's your picture, worked from the one you gave me."
              : "Here's what I saw when you said that.",
            imageDataUrl: src,
            imagePrompt: prompt,
            imageProvider: data.provider || "gemini",
            imageModelLabel: data.model_label || null,
            castUsed: data.cast_used || [],
            castHint,
            imageCreationId: data.creation_id || null,
            imageFallbackReason: data.fallback_reason || null,
          };
        }
        return copy;
      });
    } catch (e) {
      console.warn("create image failed:", e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // 400 (guardrail) and 402 (out of credits) both carry a human message
      // from the server — show it verbatim. A 422 means we sent something the
      // API doesn't accept, which is our bug, not the user's prompt: say so
      // rather than blaming the wording.
      const hasHumanDetail = (status === 400 || status === 402) && typeof detail === "string";
      if (status === 422) console.error("create image rejected the request:", detail);
      setMsgs(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.imageLoading) {
          copy[copy.length - 1] = {
            role: "assistant",
            content: hasHumanDetail
              ? detail
              : status === 422
                ? "Something in that request didn't fit — I've logged it. Try asking again?"
                : "Sorry — image generation didn't work just now. Give me another prompt?",
            error: true,
          };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [setMsgs, setBusy]);

  const createVideo = useCallback(async (prompt, { size = "1280x720", duration = 4, inline = false } = {}) => {
    // Sora 2 clips take 2–5 minutes. Kick off a job, show a warm waiting bubble,
    // and poll every 4s until it resolves.
    setMsgs(m => [
      ...m,
      ...(inline ? [] : [{ role: "user", content: `Video: ${prompt}` }]),
      {
        role: "assistant",
        content: "",
        streaming: true,
        videoLoading: true,
        videoPrompt: prompt,
        videoSize: size,
        videoDuration: duration,
        videoStartedAt: Date.now(),
      },
    ]);
    setBusy(true);
    try {
      const { data } = await api.post("/create/video", { prompt, size, duration, model: "sora-2" });
      const jobId = data.job_id;

      const started = Date.now();
      const deadline = started + 12 * 60 * 1000;
      let final = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const poll = await api.get(`/create/video/${jobId}`);
          if (poll.data.status === "done" && poll.data.url) {
            final = poll.data;
            break;
          }
          if (poll.data.status === "error") {
            throw new Error(poll.data.error || "video generation error");
          }
        } catch (pollErr) {
          console.warn("poll:", pollErr?.message);
        }
      }
      if (!final) throw new Error("timed out waiting for video");

      // Served from object storage through our own API, so it streams instead
      // of arriving as a multi-megabyte base64 string.
      const videoDataUrl = absoluteUrl(final.url);
      setMsgs(m => {
        const copy = [...m];
        for (let k = copy.length - 1; k >= 0; k--) {
          if (copy[k]?.videoLoading) {
            copy[k] = {
              role: "assistant",
              content: "Here's your scene — I held it just how you asked.",
              videoDataUrl,
              videoPrompt: prompt,
              videoSize: size,
              videoDuration: duration,
            };
            break;
          }
        }
        return copy;
      });
    } catch (e) {
      console.warn("create video failed:", e);
      setMsgs(m => {
        const copy = [...m];
        for (let k = copy.length - 1; k >= 0; k--) {
          if (copy[k]?.videoLoading) {
            copy[k] = {
              role: "assistant",
              content: "The video didn't come through this time. Want me to try again with a shorter prompt?",
              error: true,
            };
            break;
          }
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [setMsgs, setBusy]);

  const createSound = useCallback(async (prompt, { duration_seconds = 6, loop = false, inline = false } = {}) => {
    setMsgs(m => [
      ...m,
      ...(inline ? [] : [{ role: "user", content: `Sound: ${prompt}` }]),
      {
        role: "assistant",
        content: "",
        streaming: true,
        soundLoading: true,
        soundPrompt: prompt,
        soundDuration: duration_seconds,
        soundLoop: loop,
      },
    ]);
    setBusy(true);
    try {
      const { data } = await api.post("/create/sound", { prompt, duration_seconds, loop });
      const audioDataUrl = `data:${data.mime};base64,${data.audio_base64}`;
      setMsgs(m => {
        const copy = [...m];
        for (let k = copy.length - 1; k >= 0; k--) {
          if (copy[k]?.soundLoading) {
            copy[k] = {
              role: "assistant",
              content: loop
                ? "Here's your ambience — press play and let it settle in."
                : "Here's the sound — how does it feel?",
              soundDataUrl: audioDataUrl,
              soundPrompt: prompt,
              soundDuration: data.duration_seconds || duration_seconds,
              soundLoop: loop,
            };
            break;
          }
        }
        return copy;
      });
    } catch (e) {
      console.warn("create sound failed:", e);
      setMsgs(m => {
        const copy = [...m];
        for (let k = copy.length - 1; k >= 0; k--) {
          if (copy[k]?.soundLoading) {
            copy[k] = {
              role: "assistant",
              content: "The sound didn't come through this time. Give it another try?",
              error: true,
            };
            break;
          }
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [setMsgs, setBusy]);

  /**
   * Dispatch a `<generate/>` directive the LLM emitted mid-reply to the
   * matching create pipeline. All parameters are validated / clamped so a
   * misbehaving model can't request a 60-second video or a negative volume.
   */
  const handleInlineGenerate = useCallback((tag, references = []) => {
    if (!tag || !tag.kind || !tag.prompt) return;
    const kind = String(tag.kind).toLowerCase();
    const prompt = String(tag.prompt).slice(0, 800);
    if (kind === "image") {
      const size = ["1024x1024", "1024x1536", "1536x1024"].includes(tag.size) ? tag.size : "1024x1024";
      createImage(prompt, { size, inline: true, references });
    } else if (kind === "video") {
      const duration = [4, 8, 12].includes(Number(tag.duration)) ? Number(tag.duration) : 4;
      const size = ["1280x720", "720x1280"].includes(tag.size) ? tag.size : "1280x720";
      createVideo(prompt, { size, duration, inline: true });
    } else if (kind === "sound") {
      const duration_seconds = Math.min(22, Math.max(3, Number(tag.duration) || 6));
      const loop = String(tag.loop || "").toLowerCase() === "true";
      createSound(prompt, { duration_seconds, loop, inline: true });
    }
  }, [createImage, createVideo, createSound]);

  const downloadSound = useCallback((msg) => {
    if (!msg?.soundDataUrl) return;
    const a = document.createElement("a");
    a.href = msg.soundDataUrl;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `rammy-sound-${stamp}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Sound saved");
  }, []);

  const downloadVideo = useCallback((msg) => {
    if (!msg?.videoDataUrl) return;
    const a = document.createElement("a");
    a.href = msg.videoDataUrl;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `rammy-scene-${stamp}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Video saved");
  }, []);

  const keepImageMemory = useCallback(async (msg) => {
    if (!msg?.imageDataUrl) return;
    try {
      await api.post("/memories", {
        title: (msg.imagePrompt || "Generated image").slice(0, 90),
        detail: `Rammy drew this from: "${msg.imagePrompt || ""}"`,
        category: "Personal",
        has_image: true,
        thumbnail: msg.imageDataUrl,
        thumbnail_mime: "image/png",
      });
      toast.success("Kept in your Memory Garden");
      setMsgs(m => m.map(x => x === msg ? { ...x, kept: true } : x));
    } catch (e) {
      console.warn("keep memory failed:", e);
      toast.error("Couldn't save. Try again?");
    }
  }, [setMsgs]);

  const shareImageAsCard = useCallback(async (msg, idx) => {
    if (!msg?.imageDataUrl || sharingIdx >= 0) return;
    setSharingIdx(idx);
    try {
      const { share } = await createOneTapCard({
        imageSrc: msg.imageDataUrl,
        caption: msg.imagePrompt || "A moment made with Rammy.",
        robotName: "Rammy",
      });
      const outcome = await share();
      if (outcome === "download") toast.success("Card saved");
      else if (outcome === "shared") toast.success("Shared");
    } catch (e) {
      console.warn("share card failed:", e);
      toast.error("Couldn't build the card. Try again?");
    } finally {
      setSharingIdx(-1);
    }
  }, [sharingIdx]);

  const onImageEdited = useCallback((newDataUrl, editPrompt) => {
    setMsgs(m => [
      ...m,
      { role: "user", content: `Edit: ${editPrompt}` },
      {
        role: "assistant",
        content: "Here's the edit — how does it feel?",
        imageDataUrl: newDataUrl,
        imagePrompt: editPrompt,
      },
    ]);
    toast.success("Edit ready");
  }, [setMsgs]);

  return {
    createImage, createVideo, createSound,
    handleInlineGenerate,
    downloadSound, downloadVideo,
    keepImageMemory, shareImageAsCard, onImageEdited,
    sharingIdx,
  };
}
